// web-frontend/src/shared/utils/storage.js - 개선 버전
import logger from './logger';
// Storage keys constants
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
  USER_ID: 'user_id',
  DEVICE_TOKEN: 'device_token',
  LANGUAGE: 'app_language',
  THEME: 'app_theme',
  SETTINGS: 'app_settings',
  CACHED_DATA: 'cached_data_',
  ONBOARDING_COMPLETED: 'hasCompletedOnboarding',
  SURVEY_COMPLETED: 'hasCompletedOnboardingSurvey',
  BIOMETRIC_ENABLED: 'biometric_enabled',
  AUTO_LOGIN: 'auto_login_enabled',
};

// 🔥 개발용 사용자 ID
export const DEV_USER_ID = 'c66e2ef5-d14a-4f58-ac67-a5ed0c2f90be';

/**
 * 🔥 localStorage 래퍼 - AsyncStorage API와 호환
 */
class LocalStorageWrapper {
  async getItem(key) {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      logger.error(`Failed to get item ${key}:`, error);
      return null;
    }
  }

  async setItem(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      logger.error(`Failed to set item ${key}:`, error);
      throw error;
    }
  }

  async removeItem(key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.error(`Failed to remove item ${key}:`, error);
      throw error;
    }
  }

  async clear() {
    try {
      localStorage.clear();
    } catch (error) {
      logger.error('Failed to clear storage:', error);
      throw error;
    }
  }

  async getAllKeys() {
    try {
      return Object.keys(localStorage);
    } catch (error) {
      logger.error('Failed to get all keys:', error);
      return [];
    }
  }

  async multiRemove(keys) {
    try {
      keys.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      logger.error('Failed to remove multiple items:', error);
      throw error;
    }
  }
}

const storage = new LocalStorageWrapper();

/**
 * Generic storage functions for OBJECTS
 */
export const storeItem = async (key, value) => {
  try {
    const jsonValue = JSON.stringify(value);
    await storage.setItem(key, jsonValue);
    logger.debug(`Storage: Stored item ${key}`);
    return true;
  } catch (error) {
    logger.error(`Failed to store item ${key}:`, error);
    return false;
  }
};

export const getStoredItem = async (key) => {
  try {
    const jsonValue = await storage.getItem(key);
    const result = jsonValue != null ? JSON.parse(jsonValue) : null;
    logger.debug(`Storage: Retrieved item ${key}:`, result ? 'Found' : 'Not found');
    return result;
  } catch (error) {
    logger.error(`Failed to get item ${key}:`, error);
    return null;
  }
};

export const removeItem = async (key) => {
  try {
    await storage.removeItem(key);
    logger.debug(`Storage: Removed item ${key}`);
    return true;
  } catch (error) {
    logger.error(`Failed to remove item ${key}:`, error);
    return false;
  }
};

export const clearAll = async () => {
  try {
    await storage.clear();
    logger.debug('Storage: All data cleared');
    return true;
  } catch (error) {
    logger.error('Failed to clear all storage:', error);
    return false;
  }
};

export const getAllKeys = async () => {
  try {
    return await storage.getAllKeys();
  } catch (error) {
    logger.error('Failed to get all keys:', error);
    return [];
  }
};

/**
 * Authentication related storage functions
 */
export const storeToken = async (token, type = 'auth_token') => {
  const key = type === 'refresh_token' ? STORAGE_KEYS.REFRESH_TOKEN : STORAGE_KEYS.AUTH_TOKEN;
  try {
    await storage.setItem(key, token);
    logger.debug(`Storage: Stored ${type} token`);
    return true;
  } catch (error) {
    logger.error(`Failed to store token ${key}:`, error);
    return false;
  }
};

export const getStoredToken = async (type = 'auth_token') => {
  const key = type === 'refresh_token' ? STORAGE_KEYS.REFRESH_TOKEN : STORAGE_KEYS.AUTH_TOKEN;
  try {
    const token = await storage.getItem(key);
    logger.debug(`Storage: Retrieved ${type} token:`, token ? 'Found' : 'Not found');
    return token;
  } catch (error) {
    logger.error(`Failed to get token ${key}:`, error);
    return null;
  }
};

export const removeStoredToken = async (type = 'auth_token') => {
  const key = type === 'refresh_token' ? STORAGE_KEYS.REFRESH_TOKEN : STORAGE_KEYS.AUTH_TOKEN;
  return await removeItem(key);
};

/**
 * 🔥 JWT 토큰 디코딩 - 페이로드 추출 (신규 추가)
 */
const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    logger.error('JWT 디코딩 실패:', error);
    return null;
  }
};

/**
 * 🔥 JWT 토큰 만료 확인 (기존 + 개선)
 */
export const isTokenExpired = (token) => {
  if (!token || typeof token !== 'string') {
    logger.warn('⚠️ 유효하지 않은 토큰 형식');
    return true;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      logger.warn('⚠️ JWT 형식이 올바르지 않음');
      return true;
    }

    // 🔥 개선: decodeJWT 함수 사용
    const decoded = decodeJWT(token);
    
    if (!decoded || !decoded.exp) {
      logger.warn('⚠️ 토큰에 exp 필드 없음');
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const isExpired = decoded.exp < currentTime;

    if (isExpired) {
      const expirationDate = new Date(decoded.exp * 1000);
      logger.debug(`⏰ 토큰 만료됨: ${expirationDate.toISOString()}`);
    } else {
      const timeLeft = decoded.exp - currentTime;
      const hoursLeft = Math.floor(timeLeft / 3600);
      logger.debug(`✅ 토큰 유효: ${hoursLeft}시간 남음`);
    }

    return isExpired;
  } catch (error) {
    logger.error('❌ 토큰 검증 실패:', error);
    return true;
  }
};

/**
 * 🔥 저장된 토큰의 유효성 확인 (개선 버전)
 */
export const validateStoredToken = async () => {
  try {
    logger.debug('🔍 인증 데이터 검증 시작...');
    
    const token = await getStoredToken();
    
    if (!token) {
      logger.debug('ℹ️ 저장된 토큰 없음');
      return { valid: false, token: null };
    }

    // 🔥 개선: 토큰 디코딩하여 상세 정보 포함
    const decoded = decodeJWT(token);
    
    if (!decoded || !decoded.exp) {
      logger.warn('⚠️ 토큰 디코딩 실패');
      await removeStoredToken();
      await removeStoredToken('refresh_token');
      return { valid: false, token: null, reason: 'invalid_token' };
    }

    // 만료 확인
    const now = Math.floor(Date.now() / 1000);
    const isExpired = decoded.exp < now;

    if (isExpired) {
      logger.warn('⚠️ 저장된 토큰이 만료됨 - 자동 삭제');
      await removeStoredToken();
      await removeStoredToken('refresh_token');
      await removeUserData();
      return { valid: false, token: null, reason: 'expired' };
    }

    // 🔥 개선: 만료까지 남은 시간 정보 추가
    const expiresIn = decoded.exp - now;
    
    // 만료 임박 경고 (5분 이내)
    if (expiresIn < 300) {
      logger.warn(`⚠️ 토큰이 곧 만료됩니다 (${Math.floor(expiresIn / 60)}분 남음)`);
    }

    logger.debug('✅ 저장된 토큰 유효함');
    
    // 🔥 개선: 상세 정보 반환
    return { 
      valid: true, 
      token,
      decoded,
      expiresIn,
      expiresAt: new Date(decoded.exp * 1000)
    };
  } catch (error) {
    logger.error('❌ 토큰 검증 실패:', error);
    return { valid: false, token: null, reason: 'error' };
  }
};

export const storeUserData = async (userData) => {
  return await storeItem(STORAGE_KEYS.USER_DATA, userData);
};

export const getStoredUserData = async () => {
  return await getStoredItem(STORAGE_KEYS.USER_DATA);
};

export const removeUserData = async () => {
  return await removeItem(STORAGE_KEYS.USER_DATA);
};

export const isAuthenticated = async () => {
  const token = await getStoredToken();
  if (!token) return false;
  
  // 🔥 개선: 토큰 만료도 확인
  return !isTokenExpired(token);
};

/**
 * 🔥 UUID 형식 검증
 */
export const isValidUUID = (str) => {
  if (!str || typeof str !== 'string') return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const storeUserId = async (userId) => {
  try {
    if (!isValidUUID(userId)) {
      logger.warn('⚠️ 유효하지 않은 UUID 형식:', userId);
      return false;
    }
    await storage.setItem(STORAGE_KEYS.USER_ID, userId);
    logger.debug(`Storage: User ID 저장됨 ${userId}`);
    return true;
  } catch (error) {
    logger.error('Failed to store user ID:', error);
    return false;
  }
};

export const getStoredUserId = async () => {
  try {
    const storedId = await storage.getItem(STORAGE_KEYS.USER_ID);
    
    if (storedId && isValidUUID(storedId)) {
      logger.debug(`✅ 유효한 User ID 조회: ${storedId}`);
      return storedId;
    }

    if (storedId) {
      logger.warn(`⚠️ 잘못된 User ID 감지: ${storedId}`);
      await storage.removeItem(STORAGE_KEYS.USER_ID);
      logger.debug('✅ 잘못된 User ID 삭제됨');
    }

    logger.debug('ℹ️ 저장된 User ID 없음 - 로그인이 필요합니다');
    return null;
  } catch (error) {
    logger.error('Failed to get stored user ID:', error);
    return null;
  }
};

export const removeUserId = async () => {
  try {
    await storage.removeItem(STORAGE_KEYS.USER_ID);
    logger.debug('Storage: User ID 삭제됨');
    return true;
  } catch (error) {
    logger.error('Failed to remove user ID:', error);
    return false;
  }
};

/**
 * Onboarding related storage functions
 */
export const isOnboardingCompleted = async () => {
  try {
    const completed = await storage.getItem(STORAGE_KEYS.ONBOARDING_COMPLETED);
    return completed === 'true';
  } catch (error) {
    logger.error('Failed to check onboarding status:', error);
    return false;
  }
};

export const setOnboardingCompleted = async (completed = true) => {
  try {
    await storage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETED, completed ? 'true' : 'false');
    logger.debug(`Storage: Onboarding status set to ${completed}`);
    return true;
  } catch (error) {
    logger.error('Failed to set onboarding status:', error);
    return false;
  }
};

export const resetOnboarding = async () => {
  try {
    await storage.multiRemove([
      STORAGE_KEYS.ONBOARDING_COMPLETED,
      STORAGE_KEYS.SURVEY_COMPLETED
    ]);
    logger.debug('Storage: Onboarding status reset');
    return true;
  } catch (error) {
    logger.error('Failed to reset onboarding:', error);
    return false;
  }
};

export const isSurveyCompleted = async () => {
  try {
    const completed = await storage.getItem(STORAGE_KEYS.SURVEY_COMPLETED);
    return completed === 'true';
  } catch (error) {
    logger.error('Failed to check survey status:', error);
    return false;
  }
};

export const setSurveyCompleted = async (completed = true) => {
  try {
    await storage.setItem(STORAGE_KEYS.SURVEY_COMPLETED, completed ? 'true' : 'false');
    logger.debug(`Storage: Survey status set to ${completed}`);
    return true;
  } catch (error) {
    logger.error('Failed to set survey status:', error);
    return false;
  }
};

/**
 * App settings related storage functions
 */
export const storeLanguage = async (language) => {
  return await storeItem(STORAGE_KEYS.LANGUAGE, language);
};

export const getStoredLanguage = async () => {
  const language = await getStoredItem(STORAGE_KEYS.LANGUAGE);
  return language || 'en';
};

export const storeTheme = async (theme) => {
  return await storeItem(STORAGE_KEYS.THEME, theme);
};

export const getStoredTheme = async () => {
  const theme = await getStoredItem(STORAGE_KEYS.THEME);
  return theme || 'light';
};

export const storeSettings = async (settings) => {
  return await storeItem(STORAGE_KEYS.SETTINGS, settings);
};

export const getStoredSettings = async () => {
  const settings = await getStoredItem(STORAGE_KEYS.SETTINGS);
  return settings || {};
};

export const updateSetting = async (key, value) => {
  try {
    const currentSettings = await getStoredSettings();
    const updatedSettings = { ...currentSettings, [key]: value };
    return await storeSettings(updatedSettings);
  } catch (error) {
    logger.error(`Failed to update setting ${key}:`, error);
    return false;
  }
};

/**
 * Cache management functions
 */
export const storeCache = async (key, data, expiryMinutes = 30) => {
  try {
    const cacheItem = {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + (expiryMinutes * 60 * 1000),
    };
    return await storeItem(STORAGE_KEYS.CACHED_DATA + key, cacheItem);
  } catch (error) {
    logger.error(`Failed to store cache ${key}:`, error);
    return false;
  }
};

export const getCache = async (key) => {
  try {
    const cacheItem = await getStoredItem(STORAGE_KEYS.CACHED_DATA + key);
    
    if (!cacheItem) {
      return null;
    }

    if (Date.now() > cacheItem.expiry) {
      await removeItem(STORAGE_KEYS.CACHED_DATA + key);
      logger.debug(`Storage: Cache ${key} expired and removed`);
      return null;
    }

    logger.debug(`Storage: Cache ${key} retrieved`);
    return cacheItem.data;
  } catch (error) {
    logger.error(`Failed to get cache ${key}:`, error);
    return null;
  }
};

export const clearCache = async (key) => {
  return await removeItem(STORAGE_KEYS.CACHED_DATA + key);
};

export const clearAllCache = async () => {
  try {
    const keys = await getAllKeys();
    const cacheKeys = keys.filter(key => key.startsWith(STORAGE_KEYS.CACHED_DATA));
    await Promise.all(cacheKeys.map(key => removeItem(key)));
    logger.debug(`Storage: Cleared ${cacheKeys.length} cache items`);
    return true;
  } catch (error) {
    logger.error('Failed to clear all cache:', error);
    return false;
  }
};

/**
 * Clear all authentication data
 */
export const clearAuthData = async () => {
  try {
    await Promise.all([
      removeStoredToken('auth_token'),
      removeStoredToken('refresh_token'),
      removeUserData(),
      removeUserId(),
    ]);
    logger.debug('Storage: Auth data cleared');
    return true;
  } catch (error) {
    logger.error('Failed to clear auth data:', error);
    return false;
  }
};

/**
 * 🔥 디버깅용 함수 - 인증 상태 상세 출력 (신규 추가)
 */
export const debugStorage = async () => {
  logger.debug('='.repeat(50));
  logger.debug('📦 Storage Debug Info');
  logger.debug('='.repeat(50));
  
  const token = await getStoredToken();
  const userData = await getStoredUserData();
  const userId = await getStoredUserId();
  const validation = await validateStoredToken();
  
  logger.debug('Token:', token ? token.substring(0, 20) + '...' : 'null');
  logger.debug('User:', userData?.email || 'null');
  logger.debug('User ID:', userId || 'null');
  logger.debug('Valid:', validation.valid);
  
  if (validation.valid && validation.decoded) {
    logger.debug('Expires in:', validation.expiresIn, 'seconds');
    logger.debug('Expires at:', validation.expiresAt?.toLocaleString() || 'unknown');
  }
  
  logger.debug('='.repeat(50));
};

/**
 * Get storage info (for debugging)
 */
export const getStorageInfo = async () => {
  try {
    const keys = await getAllKeys();
    const info = {
      totalKeys: keys.length,
      authKeys: keys.filter(key =>
        key === STORAGE_KEYS.AUTH_TOKEN ||
        key === STORAGE_KEYS.REFRESH_TOKEN ||
        key === STORAGE_KEYS.USER_DATA ||
        key === STORAGE_KEYS.USER_ID
      ),
      cacheKeys: keys.filter(key => key.startsWith(STORAGE_KEYS.CACHED_DATA)),
      settingsKeys: keys.filter(key =>
        key === STORAGE_KEYS.LANGUAGE ||
        key === STORAGE_KEYS.THEME ||
        key === STORAGE_KEYS.SETTINGS
      ),
    };

    // 🔥 개선: 더 상세한 토큰 정보
    const tokenValidation = await validateStoredToken();
    info.tokenInfo = {
      hasToken: !!tokenValidation.token,
      isValid: tokenValidation.valid,
      expiresIn: tokenValidation.expiresIn,
      expiresAt: tokenValidation.expiresAt,
    };

    return info;
  } catch (error) {
    logger.error('Failed to get storage info:', error);
    return {
      totalKeys: 0,
      authKeys: [],
      cacheKeys: [],
      settingsKeys: [],
      error: error.message,
    };
  }
};

export default {
  storeItem,
  getStoredItem,
  removeItem,
  clearAll,
  getAllKeys,
  storeToken,
  getStoredToken,
  removeStoredToken,
  storeUserData,
  getStoredUserData,
  removeUserData,
  isAuthenticated,
  isTokenExpired,
  validateStoredToken,
  storeUserId,
  getStoredUserId,
  removeUserId,
  isValidUUID,
  DEV_USER_ID,
  isOnboardingCompleted,
  setOnboardingCompleted,
  resetOnboarding,
  isSurveyCompleted,
  setSurveyCompleted,
  storeLanguage,
  getStoredLanguage,
  storeTheme,
  getStoredTheme,
  storeSettings,
  getStoredSettings,
  updateSetting,
  storeCache,
  getCache,
  clearCache,
  clearAllCache,
  getStorageInfo,
  clearAuthData,
  debugStorage, // 🔥 신규 추가
  STORAGE_KEYS,
};